import { Link } from 'react-router-dom';
import { useSiteMasterSession } from '@/hooks/useSiteMasterSession';
import { siteMasterPaths } from '@/lib/siteMasterPaths';
import { cn } from '@/lib/cn';

const rowActionClass =
  'flex w-full items-center gap-4 rounded-3xl px-5 py-4 text-left font-bold shadow-sm ring-1 transition active:scale-[0.99]';

export function SiteMasterHomePage() {
  const { master, currentSite, currentSiteId, sites, promotionAreas, hasMpAnalytics } = useSiteMasterSession();
  const hasSites = sites.length > 0;
  const hasMp = (promotionAreas?.length ?? 0) > 0;
  const siteLabel = currentSiteId === 0 ? '全部' : (currentSite?.site_name ?? '');

  return (
    <div className="space-y-5">
      <section className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200/80">
        <h1 className="text-2xl font-bold text-slate-900">
          {master?.display_name ? `${master.display_name}，您好` : '您好'}
        </h1>
        <p className="mt-3 text-lg leading-relaxed text-slate-600">
          {hasSites ? (
            <>
              您正在管理「<strong className="text-slate-800">{siteLabel}</strong>」站点信息。
            </>
          ) : hasMp ? (
            <>您已开通微推广区域管理，可在下方进入。</>
          ) : hasMpAnalytics ? (
            <>您已开通小程序访问统计，可在下方查看数据。</>
          ) : (
            <>请联系管理员分配站点或推广区域。</>
          )}
        </p>
        {sites.length > 1 ? (
          <p className="mt-2 text-base text-slate-500">顶部可切换其他站点。</p>
        ) : null}
      </section>

      {(hasSites || hasMp || hasMpAnalytics) ? (
        <nav className="flex flex-col gap-3" aria-label="功能入口">
          {hasSites ? (
            <>
              <Link
                to={siteMasterPaths.entries}
                className={cn(
                  rowActionClass,
                  'border-2 border-teal-600/30 bg-white text-teal-900 ring-teal-100 active:bg-teal-50',
                )}
              >
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-teal-50 text-teal-700">
                  <i className="fa-solid fa-list text-2xl" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-xl">站点信息</span>
                  <span className="mt-0.5 block text-base font-normal text-slate-600">查看、编辑与添加信息</span>
                </span>
                <i className="fa-solid fa-chevron-right shrink-0 text-lg text-slate-400" aria-hidden />
              </Link>
              <Link
                to={siteMasterPaths.ads}
                className={cn(
                  rowActionClass,
                  'border-2 border-amber-500/35 bg-white text-amber-950 ring-amber-100 active:bg-amber-50',
                )}
              >
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-amber-50 text-amber-700">
                  <i className="fa-solid fa-rectangle-ad text-2xl" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-xl">站点广告</span>
                  <span className="mt-0.5 block text-base font-normal text-slate-600">首页横条广告管理</span>
                </span>
                <i className="fa-solid fa-chevron-right shrink-0 text-lg text-slate-400" aria-hidden />
              </Link>
            </>
          ) : null}
          {hasMp ? (
            <Link
              to={siteMasterPaths.mp}
              className={cn(
                rowActionClass,
                hasSites
                  ? 'border-2 border-violet-600/30 bg-white text-violet-900 ring-violet-100 active:bg-violet-50'
                  : 'border-2 border-violet-600 bg-violet-600 text-white ring-violet-700/20 active:bg-violet-700',
              )}
            >
              <span
                className={cn(
                  'flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl',
                  hasSites ? 'bg-violet-50 text-violet-700' : 'bg-white/15 text-white',
                )}
              >
                <i className="fa-solid fa-bullhorn text-2xl" aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-xl">微推广信息</span>
                <span
                  className={cn(
                    'mt-0.5 block text-base font-normal',
                    hasSites ? 'text-slate-600' : 'text-violet-100',
                  )}
                >
                  按区域管理推广条目
                </span>
              </span>
              <i
                className={cn(
                  'fa-solid fa-chevron-right shrink-0 text-lg',
                  hasSites ? 'text-slate-400' : 'text-violet-200',
                )}
                aria-hidden
              />
            </Link>
          ) : null}
          {hasMpAnalytics ? (
            <Link
              to={siteMasterPaths.analytics}
              className={cn(
                rowActionClass,
                'border-2 border-indigo-600/30 bg-white text-indigo-900 ring-indigo-100 active:bg-indigo-50',
              )}
            >
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-700">
                <i className="fa-solid fa-chart-line text-2xl" aria-hidden />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-xl">访问统计</span>
                <span className="mt-0.5 block text-base font-normal text-slate-600">小程序 UV、拨号与发布趋势</span>
              </span>
              <i className="fa-solid fa-chevron-right shrink-0 text-lg text-slate-400" aria-hidden />
            </Link>
          ) : null}
        </nav>
      ) : null}

      <section className="rounded-3xl border-2 border-amber-200 bg-amber-50 p-5">
        <p className="text-lg font-semibold text-amber-950">温馨提示</p>
        <ul className="mt-3 list-inside list-disc space-y-2 text-base leading-relaxed text-amber-900">
          {hasSites ? <li>站点信息修改后小程序展示会随之更新</li> : null}
          {hasSites ? <li>广告需在小程序运营扩展中开启后才会展示</li> : null}
          {hasMp ? <li>微推广信息由机器人按区域配置转发，与站点无关</li> : null}
          {hasMpAnalytics ? <li>访问统计按管理员绑定的小程序展示，主从小程序数据独立统计</li> : null}
          <li>删除信息不可恢复，请谨慎操作</li>
          <li>如有疑问请联系系统管理员</li>
        </ul>
      </section>
    </div>
  );
}
